import { normalizeGenerationError } from '@/utils/generationErrors';

const sanitizeStreamLine = (line = '') => {
  return String(line)
    .replace(/sk-[A-Za-z0-9]{32,}/g, 'sk-***')
    .replace(/Bearer\s+[A-Za-z0-9_-]+/gi, 'Bearer ***')
    .replace(/"token":\s*"[^"]{100,}"/g, '"token":"[REDACTED]"')
    .substring(0, 200);
};

const omitUndefinedValues = (patch = {}) => {
  return Object.fromEntries(
    Object.entries(patch).filter(([, value]) => value !== undefined)
  );
};

const applyAdditionalInfoPatch = (onAdditionalInfoPatch, patch) => {
  if (typeof onAdditionalInfoPatch === 'function') {
    onAdditionalInfoPatch(omitUndefinedValues(patch));
  }
};

const clearErrorState = (state) => {
  state.error = null;
  state.errorMessage = null;
  state.errorCode = null;
};

const handleStreamEvent = (data, state, { onAdditionalInfoPatch, onPreviewUrl } = {}) => {
  if (data.type) {
    console.log(`Streamed data type: ${data.type}`);
  }

  if (data.type === 'progress') {
    console.log('Progress:', data.progressInfo);
    applyAdditionalInfoPatch(onAdditionalInfoPatch, {
      modelName: data.modelName,
      model: data.model,
      provider: data.provider,
      taskId: data.taskId,
      progress: typeof data.progress === 'number' ? data.progress : null,
      progressInfo: data.progressInfo,
    });
    return;
  }

  if (data.type === 'task_created') {
    clearErrorState(state);
    console.log('Task created:', data.taskId);
    applyAdditionalInfoPatch(onAdditionalInfoPatch, {
      taskCreated: true,
      videoId: data.videoId,
      imageId: data.imageId,
      mediaType: data.mediaType,
      taskId: data.taskId,
      provider: data.provider,
      modelName: data.modelName,
      model: data.model,
      size: data.size,
      duration: data.duration,
      quality: data.quality,
    });
    return;
  }

  if (data.type === 'preview_url') {
    console.log('Data Preview URL received:', data.previewUrl);
    if (typeof onPreviewUrl === 'function') {
      onPreviewUrl(data.previewUrl);
    }
    applyAdditionalInfoPatch(onAdditionalInfoPatch, {
      previewUrl: data.previewUrl,
      provider: data.provider,
      modelName: data.modelName,
      model: data.model,
    });
    return;
  }

  if (data.type === 'task_submitted') {
    clearErrorState(state);
    console.log('Task submitted successfully:', data.message);
    console.log('Preview URL:', data.previewUrl);
    state.finalData = {
      type: 'task_submitted',
      videoId: data.videoId,
      imageId: data.imageId,
      mediaType: data.mediaType,
      taskId: data.taskId,
      previewUrl: data.previewUrl,
      message: data.message,
      note: data.note,
      provider: data.provider,
      modelName: data.modelName,
      model: data.model,
      additionalInfo: data.additionalInfo || null,
    };
    return;
  }

  if (data.type === 'task_queued') {
    clearErrorState(state);
    state.finalData = {
      type: 'task_queued',
      videoId: data.videoId,
      imageId: data.imageId,
      mediaType: data.mediaType,
      taskId: data.taskId || null,
      message: data.message,
      provider: data.provider || null,
      additionalInfo: data.additionalInfo || null,
    };
    return;
  }

  if (data.type === 'task_polling_failed') {
    clearErrorState(state);
    console.log('Task created but polling failed:', data.message);
    console.log('Task ID:', data.taskId);
    console.log('Provider:', data.provider);
    state.finalData = {
      type: 'task_polling_failed',
      videoId: data.videoId,
      imageId: data.imageId,
      mediaType: data.mediaType,
      taskId: data.taskId,
      message: data.message,
      note: data.note,
      provider: data.provider,
      previewUrl: null,
      modelName: data.lastError?.provider || data.provider,
      model: data.model,
      additionalInfo: data.additionalInfo || null,
    };
    return;
  }

  if (data.type === 'complete') {
    clearErrorState(state);
    state.finalData = data;
    return;
  }

  if (data.type === 'error' || data.error) {
    const normalizedError = normalizeGenerationError(
      data.error === true
        ? {
            code: data.code || state.errorCode,
            message: data.message || 'Generation failed',
            values: data.values,
          }
        : (
            data.error ||
            {
              code: data.code || state.errorCode,
              message: data.message || 'Generation failed',
            }
          ),
      {
        fallbackCode: data.code || state.errorCode || 'unknown',
        fallbackMessage: data.message || 'Generation failed',
      }
    );
    state.error = normalizedError;
    state.errorMessage = normalizedError.message;
    state.errorCode = normalizedError.code || state.errorCode;
    console.warn('Received error from backend:', normalizedError.message);
    return;
  }

  if (!data.type && !data.error && (data.data || data.url)) {
    clearErrorState(state);
    state.finalData = data;
  }
};

export async function parseGenerationStream(response, callbacks = {}) {
  const reader = response.body?.getReader?.();
  if (!reader) {
    throw new Error('Streaming response unavailable');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  const state = {
    finalData: null,
    error: null,
    errorMessage: null,
    errorCode: null,
  };

  const parseChunk = (payload, isFinalBuffer = false) => {
    try {
      const data = JSON.parse(payload);
      handleStreamEvent(data, state, callbacks);
    } catch (parseError) {
      if (isFinalBuffer) {
        console.error('Parse final buffer error:', parseError);
        return;
      }

      console.error('Parse error:', parseError.message, 'Line preview:', sanitizeStreamLine(payload));
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.trim()) continue;
      parseChunk(line);
    }
  }

  if (buffer.trim()) {
    parseChunk(buffer, true);
  }

  return state;
}
